<template>
  <div class="header-wrapper">
    <nav class="top-nav">
      <div class="logo">
        <div class="logo-wordmark" aria-label="大赛官方报名">
          <span class="logo-wordmark__title">大赛官方报名</span>
        </div>
      </div>

      <ul class="nav-menu">
        <li
          v-for="(menu, index) in menuList"
          :key="index"
          :class="{ dropdown: Array.isArray(menu.child) && menu.child.length > 0 }"
        >
          <router-link
            v-if="!menu.child"
            class="router-tag"
            tag="span"
            :to="menu.path"
            :class="{ active: activeKey === index }"
            @click.native="handleNavClick(menu.path, index)"
          >
            {{ menu.name }}
          </router-link>

          <span v-else class="router-tag dropdown-trigger">{{ menu.name }}</span>

          <div v-if="menu.child" class="dropdown-content">
            <span
              v-for="(submenu, subIndex) in menu.child"
              :key="subIndex"
              @click="handleNavClick(submenu.path, index)"
            >
              {{ submenu.name }}
            </span>
          </div>
        </li>
      </ul>

      <div class="login-status">
        <div
          v-if="isLogin"
          class="user-center-entry"
          @click="handleClickLogin"
        >
          <img
            :src="displayAvatar"
            alt="avatar"
          >
          <span class="user-center-entry__text">个人中心</span>
        </div>
        <div v-else class="login-actions">
          <span @click="handleClickLogin">登录</span>
          <span @click="handleClickRegister">注册</span>
        </div>
        <a-popconfirm
          v-if="isLogin"
          title="确认退出当前账号吗？"
          ok-text="退出"
          cancel-text="取消"
          @confirm="handleLogout"
        >
          <div class="logout">退出</div>
        </a-popconfirm>
      </div>
    </nav>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import cookie from "@/utils/store/cookie";
import { authLogout } from "@/api/Login";
import { activityDetail, getActivityList, getExpertVoteAccess } from "@/api/Vote";
import { toDisplayImageUrl } from "@/utils/workImage";
import toLogin from "@/utils/login";
import {
  DEFAULT_GRADUATION_ACTIVITY_ID,
  CASHMERE_ACTIVITY_ID,
} from "@/utils/contestActivity";

function parseDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const text = String(value).trim();
  if (!text) return null;

  const direct = new Date(text.includes("T") ? text : text.replace(" ", "T"));
  if (!Number.isNaN(direct.getTime())) return direct;

  const fallback = new Date(text.replace(/-/g, "/"));
  if (!Number.isNaN(fallback.getTime())) return fallback;

  return null;
}

function resolveVisibleVoteRounds(activity) {
  if (!activity) return [];
  const rounds = Array.isArray(activity.rounds) ? activity.rounds : [];
  return rounds.filter(
    (item) => item && Number(item.isShow) === 1 && parseDateTime(item.startTime)
  );
}

function resolveVoteStartTime(activity) {
  const visibleRounds = resolveVisibleVoteRounds(activity);
  if (visibleRounds.length === 0) {
    return null;
  }
  const sorted = visibleRounds
    .map((item) => parseDateTime(item.startTime))
    .filter(Boolean)
    .sort((a, b) => a.getTime() - b.getTime());
  return sorted[0] || null;
}

export default {
  name: "HeaderModule",
  data() {
    const allMenuList = [
      {
        name: "Young·绒—2026第二届中国创新创意设计大赛",
        activityId: CASHMERE_ACTIVITY_ID,
        path: "/ContestInfo/cashmere-design",
        child: [
          { name: "赛事介绍", path: "/ContestInfo/cashmere-design" },
          { name: "作品投票", path: `/WorkCompetition/${CASHMERE_ACTIVITY_ID}` },
          { name: "立即报名", path: `/ContestApply/${CASHMERE_ACTIVITY_ID}` },
        ],
      },
      {
        name: "2026第五届全国大学生毕业季工艺美术类优秀毕业作品大赛",
        activityId: DEFAULT_GRADUATION_ACTIVITY_ID,
        path: "/Graduation",
        child: [
          { name: "赛事介绍", path: "/Graduation" },
          { name: "作品投票", path: `/WorkCompetition/${DEFAULT_GRADUATION_ACTIVITY_ID}` },
          { name: "立即报名", path: `/ContestApply/${DEFAULT_GRADUATION_ACTIVITY_ID}` },
        ],
      },
    ];
    return {
      activeKey: 0,
      defaultAvatar: require("@/assets/img/user.png"),
      allMenuList,
      menuList: allMenuList.slice(),
    };
  },
  computed: {
    ...mapGetters(["userInfo", "isLogin"]),
    displayAvatar() {
      return toDisplayImageUrl(this.userInfo.avatar) || this.defaultAvatar;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(route) {
        this.syncActiveKey(route);
      },
    },
    isLogin() {
      this.syncVisibleMenus();
    },
    "userInfo.uid"() {
      if (this.isLogin) {
        this.syncVisibleMenus();
      }
    },
  },
  created() {
    this.syncVisibleMenus();
  },
  methods: {
    shouldShowVoteEntry(activity, expertAccess) {
      if (!this.isLogin || !expertAccess || Number(expertAccess.expertUser) !== 1) {
        return false;
      }
      if (!activity || Number(activity.isVote) !== 1) {
        return false;
      }
      if (resolveVisibleVoteRounds(activity).length === 0) {
        return false;
      }
      const openAt = resolveVoteStartTime(activity);
      if (!openAt) {
        return false;
      }
      return Date.now() >= openAt.getTime();
    },
    buildMenuByActivity(menu, activity, expertAccess) {
      if (!menu || !Array.isArray(menu.child)) {
        return menu;
      }
      const allowVote = this.shouldShowVoteEntry(activity, expertAccess);
      return {
        ...menu,
        child: menu.child.filter((item) => item.name !== "作品投票" || allowVote),
      };
    },
    syncActiveKey(route) {
      const matchedIndex = this.menuList.findIndex((menu) => {
        if (menu.path === route.path) {
          return true;
        }
        return Array.isArray(menu.child) && menu.child.some((item) => item.path === route.path);
      });
      this.activeKey = matchedIndex >= 0 ? matchedIndex : 0;
    },
    async syncVisibleMenus() {
      try {
        const res = await getActivityList({ page: 1, limit: 50 });
        const records = Array.isArray(res.data) ? res.data : [];
        const activityMap = new Map();
        const expertAccessMap = new Map();
        await Promise.all(
          records.map(async (item) => {
            const activityId = Number(item.id || 0);
            if (!activityId) return;
            try {
              const detailRes = await activityDetail(activityId, { isShow: 0, uid: 0 });
              activityMap.set(activityId, detailRes.data || item);
            } catch (error) {
              activityMap.set(activityId, item);
            }
            if (this.isLogin) {
              try {
                const accessRes = await getExpertVoteAccess(activityId);
                expertAccessMap.set(activityId, accessRes.data || null);
              } catch (error) {
                expertAccessMap.set(activityId, null);
              }
            }
          })
        );
        this.menuList = this.allMenuList
          .filter((menu) => !menu.activityId || activityMap.has(Number(menu.activityId)))
          .map((menu) => {
            if (!menu.activityId) {
              return menu;
            }
            return this.buildMenuByActivity(
              menu,
              activityMap.get(Number(menu.activityId)),
              expertAccessMap.get(Number(menu.activityId))
            );
          });
      } catch (error) {
        this.menuList = this.allMenuList.map((menu) => this.buildMenuByActivity(menu, null, null));
      }
      this.syncActiveKey(this.$route);
    },
    async handleNavClick(path, index) {
      if (index >= 0) {
        this.activeKey = index;
      }
      if (path && path !== this.$route.path) {
        this.$router.push(path);
      }
    },
    handleClickLogin() {
      if (this.isLogin) {
        if (this.$route.name !== "UserCenter") {
          this.$router.push({ name: "UserCenter" });
        }
        return;
      }
      cookie.set("login_back_url", this.$route.fullPath);
      this.$router.push({ name: "Login" });
    },
    handleClickRegister() {
      cookie.set("login_back_url", this.$route.fullPath);
      this.$router.push({ name: "Login", query: { tab: 4 } });
    },
    clearSession() {
      this.$store.commit("LOGOUT");
      localStorage.clear();
      cookie.clearAll();
    },
    handleLogout() {
      const currentPath = this.$route.fullPath;
      authLogout()
        .catch(() => null)
        .finally(() => {
          this.clearSession();
          if (this.$route.meta && this.$route.meta.requireAuth) {
            toLogin(false, currentPath);
          }
        });
    },
  },
};
</script>

<style scoped>
.header-wrapper {
  height: 72px;
}

.top-nav {
  width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  background-color: #fff;
  color: #333;
  padding: 10px 0 6px;
  gap: 28px;
}

.logo {
  cursor: default;
  flex: 0 0 220px;
}

.logo-wordmark {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 54px;
  padding: 4px 0 10px;
}

.logo-wordmark::after {
  content: "";
  position: absolute;
  left: 2px;
  right: 2px;
  bottom: 0;
  height: 2px;
  background: linear-gradient(90deg, rgba(194, 124, 63, 0), rgba(194, 124, 63, 0.9), rgba(194, 124, 63, 0));
}

.logo-wordmark__title {
  display: inline-block;
  color: #7b3622;
  font-size: 28px;
  line-height: 1;
  letter-spacing: 4px;
  font-weight: 600;
  white-space: nowrap;
  font-family: "STKaiti", "KaiTi", "FangSong", "Times New Roman", serif;
  background: linear-gradient(135deg, #5b2417 0%, #b96f3f 45%, #7a2f1b 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 6px 18px rgba(117, 57, 33, 0.16);
}

.nav-menu {
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex: 1;
  justify-content: center;
}

.nav-menu li {
  position: relative;
  margin: 0 14px;
  font-size: 15px;
  height: 30px;
  line-height: 30px;
}

.nav-menu li.dropdown {
  padding-bottom: 18px;
  margin-bottom: -18px;
}

.router-tag {
  display: block;
  width: 100%;
  color: #3a322c;
  white-space: nowrap;
}

.router-tag:hover,
.router-tag.active {
  height: 30px;
  line-height: 30px;
  color: #df7645;
  border-bottom: 2px solid #df7645;
}

.dropdown-trigger {
  position: relative;
  padding-right: 18px;
  cursor: default;
  transition: color 0.2s ease;
}

.dropdown-trigger::after {
  content: "";
  position: absolute;
  right: 2px;
  top: 50%;
  width: 7px;
  height: 7px;
  border-right: 2px solid #c78663;
  border-bottom: 2px solid #c78663;
  transform: translateY(-70%) rotate(45deg);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.dropdown:hover .dropdown-trigger {
  color: #df7645;
}

.dropdown:hover .dropdown-trigger::after {
  border-color: #df7645;
  transform: translateY(-25%) rotate(225deg);
}

.dropdown-content {
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  min-width: 180px;
  padding: 10px;
  border: 1px solid rgba(223, 118, 69, 0.12);
  border-radius: 16px;
  background: linear-gradient(180deg, #fffdfb 0%, #fff7f2 100%);
  box-shadow: 0 18px 40px rgba(104, 70, 49, 0.18);
  transform: translateX(-50%) translateY(8px);
  opacity: 0;
  visibility: hidden;
  z-index: 20;
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
}

.dropdown-content::before {
  content: "";
  position: absolute;
  top: -8px;
  left: 50%;
  width: 14px;
  height: 14px;
  background: #fffaf6;
  border-top: 1px solid rgba(223, 118, 69, 0.12);
  border-left: 1px solid rgba(223, 118, 69, 0.12);
  transform: translateX(-50%) rotate(45deg);
}

.dropdown-content span {
  margin-bottom: 6px;
  padding: 10px 14px;
  min-height: 42px;
  line-height: 22px;
  display: block;
  text-align: left;
  border-radius: 10px;
  color: #5a4638;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.dropdown-content span:last-child {
  margin-bottom: 0;
}

.dropdown-content span:hover {
  background: rgba(223, 118, 69, 0.1);
  color: #df7645;
  transform: translateX(2px);
}

.dropdown:hover .dropdown-content {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.login-status {
  display: flex;
  align-items: center;
  font-size: 14px;
  flex: 0 0 auto;
}

.user-center-entry {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.user-center-entry__text {
  color: #5a4638;
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
}

.login-actions {
  display: flex;
  gap: 8px;
}

.login-status span {
  color: #fff;
  padding: 3px 10px;
  background: rgba(43, 43, 43, 0.87);
  border-radius: 6px;
  cursor: pointer;
}

.login-status img {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
}

.logout {
  margin-left: 8px;
  color: #333;
  background: #fff;
  cursor: pointer;
}
</style>
